<template>
    <div>
        <div class="top-title-content">
            <span class="title">출입기록</span>
            <span class="txt">CLUe 게이트 통과 출입기록을 확인할 수 있습니다.</span>
            <button
                v-if="!accessDenied && canOpenDoor"
                class="com-btn blue"
                style="float:right;"
                :disabled="doorOpening || doorCooldown"
                @click="onOpenDoor"
            >
                {{ doorOpening ? '요청 중…' : '출입문 열기' }}
            </button>
        </div>

        <div v-if="accessDenied" class="com-search-content">
            <p>슈프리마 기기 사용 센터가 아닙니다.</p>
        </div>

        <div v-if="!accessDenied" class="com-search-content">
            <div class="line">
                <div class="com-calendar-wrap">
                    <single-calendar :start-date="startDate" @update="startDateUpdate"></single-calendar>
                    <div class="com-dash"><span class="dash">~</span></div>
                    <single-calendar :end-date="endDate" @update="endDateUpdate"></single-calendar>
                </div>
                <div class="com-search-input">
                    <input v-model="keyword" placeholder="이름 입력" type="text" v-on:keyup.enter="onSearch" />
                    <button class="search-btn" @click="onSearch">검색</button>
                </div>
            </div>
            <div class="line">
                <select-form
                    :select-data="['전체', '허용', '차단']"
                    :select-value="['ALL', 'ALLOW', 'DENY']"
                    :selected-value="resultFilter"
                    @update:set="onResultFilterChange"
                ></select-form>
                <select-form
                    :select-data="['전체', '안면', 'QR']"
                    :select-value="['ALL', 'FACE', 'QR']"
                    :selected-value="methodFilter"
                    @update:set="onMethodFilterChange"
                ></select-form>
                <select-form
                    :select-data="['전체', '회원', '직원']"
                    :select-value="['ALL', 'MEMBER', 'STAFF']"
                    :selected-value="userTypeFilter"
                    @update:set="onUserTypeFilterChange"
                ></select-form>
                <button class="com-search-reset" @click="resetSearchContent"></button>
                <button class="com-search-btn" @click="toggleAutoRefresh">
                    자동새로고침: {{ autoRefresh ? 'ON' : 'OFF' }}
                </button>
            </div>
        </div>

        <div v-if="!accessDenied" class="com-table-content">
            <table class="com-table">
                <colgroup>
                    <col style="width: 170px" />
                    <col style="width: 90px" />
                    <col style="width: 80px" />
                    <col style="width: 80px" />
                    <col style="width: 160px" />
                    <col style="width: 80px" />
                    <col style="width: 460px" />
                </colgroup>
                <thead>
                    <tr>
                        <th>시각</th>
                        <th>이름</th>
                        <th>구분</th>
                        <th>방식</th>
                        <th>단말</th>
                        <th>결과</th>
                        <th>차단사유</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(row, index) in accessList" :key="index">
                        <td>{{ formatTime(row.createdAt) }}</td>
                        <td>{{ row.userName || '-' }}</td>
                        <td>{{ userTypeLabel(row.userType) }}</td>
                        <td>{{ methodLabel(row.authMethod) }}</td>
                        <td>{{ row.deviceId || '-' }}</td>
                        <td>{{ resultLabel(row.result) }}</td>
                        <td class="left">{{ row.denyReason || '-' }}</td>
                    </tr>
                    <tr v-if="accessList.length === 0">
                        <td colspan="100">등록된 내용이 없습니다.</td>
                    </tr>
                </tbody>
            </table>
            <div class="sort-bottom-area">
                <select-form
                    :select-data="[...sortTypeList]"
                    :select-value="[...sortTypeValueList]"
                    :selected-value="pagingInfo.limit"
                    class="float-right"
                    @update:set="changeLimit"
                ></select-form>
            </div>
            <b-pagination
                v-model="pagingInfo.page"
                :per-page="pagingInfo.limit"
                :total-rows="pagingInfo.totalItems"
                align="center"
                aria-controls="my-table"
                first-class="first"
                last-class="last"
                next-class="next"
                prev-class="prev"
                @change="handlerPagenation($event)"
            ></b-pagination>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import api from '@/assets/js/api';

const pagingInfo = { page: 1, limit: 20, totalPages: 0, totalItems: 0, itemCount: 0 };

export default {
    name: 'ClueAccessLog',
    data() {
        return {
            accessList: [],
            pagingInfo: { ...pagingInfo },
            startDate: moment().format('YYYY-MM-DD'),
            endDate: moment().format('YYYY-MM-DD'),
            keyword: '',
            resultFilter: 'ALL', // ALL | ALLOW | DENY
            methodFilter: 'ALL', // ALL | FACE | QR
            userTypeFilter: 'ALL', // ALL | MEMBER | STAFF
            accessDenied: false, // 슈프리마 미사용 센터 403 게이트 — 재진입(activated) 시 재평가
            activationId: 0, // keep-alive activation 토큰 — 첫 조회 await 중 비활성화 시 폴링 재시작 레이스 방지
            autoRefresh: true,
            pollTimer: null,
            isFetching: false,
            reqSeq: 0,
            sortTypeList: [...this.$sortType],
            sortTypeValueList: [...this.$sortTypeValue],
            doorOpening: false,   // 요청 진행 중
            doorCooldown: false,  // 연타 방지 5초 (개방 시간과 무관한 고정값)
        };
    },
    computed: {
        // centerId는 Vuex 스토어 게터에서 얻는다(Attendance.vue와 동일 — prop/라우트 파라미터 불필요).
        centerId() {
            return this.$store.getters['auth/cId'];
        },
        // 개방 범위는 이 화면을 볼 수 있는 범위와 같다 — 센터직원(D)이면 직급과 무관하게 열 수 있다.
        // 웹 어드민 로그인은 대표관리자도 D로 발급되므로 C를 요구하면 버튼이 아무에게도 보이지 않는다.
        // 서버도 같은 조건이라 키오스크(K) 토큰은 여기서도 서버에서도 배제된다.
        canOpenDoor() {
            return this.$store.getters['auth/isCenterStaff'];
        },
    },
    // keepAlive 라우트라 첫 진입·재진입 모두 activated가 발화한다(destroy 대신 deactivate).
    // 초기 로드를 activated에 두어 mounted와의 중복 호출을 피한다.
    async activated() {
        this.accessDenied = false; // 재진입 시 게이트 재평가(총관리자 변경 반영은 재진입 시점)
        const myActivation = ++this.activationId; // 이 activation의 토큰
        await this.onList(); // 첫 조회로 게이트 판정을 끝낸 뒤에만
        // 첫 조회 await 동안 비활성화·재진입이 없었고(토큰 유효) 게이트 미차단일 때만 폴링 시작.
        // async라 await 중 deactivated가 발화하면 그때 pollTimer가 아직 없어 stopPolling이 무효 →
        // 토큰 불일치로 여기서 startPolling을 건너뛰어 비활성 상태 백그라운드 폴링을 막는다.
        if (myActivation === this.activationId && !this.accessDenied) this.startPolling();
    },
    deactivated() {
        this.activationId += 1; // 진행 중이던 activation continuation 무효화
        this.stopPolling();
    },
    beforeDestroy() {
        this.activationId += 1; // 진행 중이던 activation continuation 무효화(비keepAlive 경로 backstop)
        this.stopPolling();
    },
    methods: {
        getList() {
            return api.get('/admin/api/center/' + this.centerId + '/clue/access-log', {
                params: {
                    startDate: moment(this.startDate).format('YYYY-MM-DD'),
                    endDate: moment(this.endDate).format('YYYY-MM-DD'),
                    result: this.resultFilter,
                    method: this.methodFilter,
                    userType: this.userTypeFilter,
                    keyword: this.keyword,
                    page: this.pagingInfo.page,
                    limit: this.pagingInfo.limit,
                },
            });
        },
        // isPolling=true(폴링)만 in-flight 시 건너뛴다. 수동 조회는 항상 수행하되
        // reqSeq로 최신 요청만 반영(인플라이트 중 필터 변경이 유실되지 않게).
        async onList(isPolling) {
            if (this.accessDenied) return;
            if (isPolling && this.isFetching) return;
            this.reqSeq += 1;
            var seq = this.reqSeq;
            this.isFetching = true;
            try {
                var response = await this.getList();
                var data = response.data;
                if (seq !== this.reqSeq) return; // 더 최신 요청이 진행 중 → stale 무시
                if (this.accessDenied) return; // 403 게이트 확정 후 도착한 인플라이트 200 무시
                this.accessList = data.items;
                this.pagingInfo.totalPages = data.meta.totalPages;
                this.pagingInfo.page = data.meta.currentPage;
                this.pagingInfo.totalItems = data.meta.totalItems;
                this.pagingInfo.itemCount = data.meta.itemCount;
            } catch (error) {
                // 슈프리마(CLUe) 미사용 센터 403 게이트: 폴링 중단 + 안내.
                // 계약 문자열 정확 일치로 식별 — 타 403(assertCenter 등)과 오분류 방지.
                var resp = error && error.response;
                var msg = resp && resp.data && resp.data.message;
                if (resp && resp.status === 403 && msg === '슈프리마 기기 사용 센터가 아닙니다.') {
                    this.accessDenied = true;
                    this.stopPolling();
                } else if (!isPolling) {
                    // 수동 조회 실패는 사용자에게 보이게 알린다(폴링 실패는 조용히 유지)
                    alert('출입기록 조회에 실패했습니다.');
                }
            } finally {
                if (seq === this.reqSeq) this.isFetching = false; // 최신 요청만 플래그 해제
            }
        },
        startPolling() {
            this.stopPolling();
            this.pollTimer = setInterval(function() {
                if (this.accessDenied) return;
                if (!this.autoRefresh) return;
                if (this.pagingInfo.page !== 1) return; // 1페이지(라이브 뷰)에서만
                if (document.hidden) return; // 비활성 탭 건너뜀
                this.onList(true);
            }.bind(this), 10000);
        },
        stopPolling() {
            if (this.pollTimer) {
                clearInterval(this.pollTimer);
                this.pollTimer = null;
            }
        },
        toggleAutoRefresh() {
            this.autoRefresh = !this.autoRefresh;
        },
        onSearch() {
            this.pagingInfo.page = 1;
            this.onList();
        },
        handlerPagenation(event) {
            this.pagingInfo.page = event;
            this.onList();
        },
        changeLimit(val) {
            this.pagingInfo.limit = val;
            this.pagingInfo.page = 1;
            this.onList();
        },
        startDateUpdate(val) { this.startDate = val; this.onSearch(); },
        endDateUpdate(val) { this.endDate = val; this.onSearch(); },
        onResultFilterChange(val) { this.resultFilter = val; this.onSearch(); },
        onMethodFilterChange(val) { this.methodFilter = val; this.onSearch(); },
        onUserTypeFilterChange(val) { this.userTypeFilter = val; this.onSearch(); },
        resetSearchContent() {
            this.keyword = '';
            this.resultFilter = 'ALL';
            this.methodFilter = 'ALL';
            this.userTypeFilter = 'ALL';
            this.startDate = moment().format('YYYY-MM-DD');
            this.endDate = moment().format('YYYY-MM-DD');
            this.onSearch();
        },
        resultLabel(r) { return r === 'ALLOW' ? '허용' : '차단'; },
        methodLabel(m) { return m === 'FACE' ? '안면' : 'QR'; },
        userTypeLabel(t) { return t === 'MEMBER' ? '회원' : (t === 'STAFF' ? '직원' : '-'); },
        formatTime(t) { return t ? moment(t).format('YYYY-MM-DD HH:mm:ss') : '-'; },
        async onOpenDoor() {
            // 개방 시간(초)을 문구에 넣지 않는다 — 실제 시간은 CLUe 도어 설정을 따른다
            if (!confirm('출입문을 엽니다. 진행하시겠습니까?')) {
                return;
            }
            this.doorOpening = true;
            try {
                const { data } = await api.post(`/admin/api/center/${this.centerId}/clue/door/open`);
                const name = data && data.doorName ? `${data.doorName} ` : '';
                // "열었습니다"로 단정하지 않는다 — 문열림 센서가 없어 물리적 개방을 확인할 수 없다
                alert(`${name}출입문 개방을 요청했습니다.`);
                this.startDoorCooldown();
            } catch (e) {
                const body = e && e.response && e.response.data;
                alert((body && body.message) || '출입문을 열지 못했습니다.');
                // 결과 미상(network_error)은 문이 이미 열렸을 수 있다 — 즉시 재클릭으로 중복 개방되지 않게 막는다.
                // 502 계열은 서버가 CLUe에 명령을 보낸 뒤 실패했을 수 있으므로 함께 쿨다운한다.
                const status = e && e.response && e.response.status;
                if (status === 502) {
                    this.startDoorCooldown();
                }
            } finally {
                this.doorOpening = false;
            }
        },
        startDoorCooldown() {
            this.doorCooldown = true;
            setTimeout(() => { this.doorCooldown = false; }, 5000);
        },
    },
};
</script>
<style lang="scss"></style>
